import type { CategoryPrompt } from "../types";

import React from "react";

import {
  Clock,
  Droplet,
  Heart,
  Leaf,
  Palette,
  Smile,
  Star,
  Wand2,
} from "lucide-react";

export const beautyPrompts: CategoryPrompt[] = [
  {
    icon: <Star />,
    title: "Influencer Collaboration Landing Page",
    description:
      "Build a page for an influencer partnership featuring their tutorial videos, product carousels with creator commentary, and exclusive codes.",
    prompt: `Build an influencer collaboration landing page that leverages creator credibility and exclusive content to drive conversions through authentic endorsement.

**INFLUENCER/CREATOR:** e.g., "@beautybysarah" OR "Alex Chen Beauty" OR "Skincare with Kim"

**COLLABORATION TYPE:** e.g., "Curated routine" OR "Signature collection" OR "Exclusive partnership"

**Audience Type:** Choose one:
- Influencer's followers (trust their recommendations)
- Beauty enthusiasts (discovering through creator)
- Tutorial seekers (learn + buy)

**Tone & Style:**
- Creator's authentic voice
- Educational and inspiring
- Community-focused ("join us")
- Exclusive insider access feel

**CTA Guidelines:** Each CTA should:
- Reference creator ("Shop Sarah's Favorites")
- Use exclusive language ("Get Her Exact Routine")
- Include discount code prominently
- Emphasize limited collaboration

**Conversion Tactics Use:**
- Exclusive creator discount code
- Tutorial videos with product timestamps
- Creator's personal routine breakdown
- Before/after with creator's results
- Limited-edition bundling
- Countdown if time-limited collab

**Key Elements to Include:**
- **Hero Section:** Creator photo/video, collaboration headline, exclusive code display, primary CTA
- **About This Collab:** Creator intro + why they chose these products (2-3 paragraphs)
- **Exclusive Discount Code:** Large, copy-able code with savings % and expiration
- **Tutorial Video(s):** 1-3 embedded videos showing product use with timestamps
- **Creator's Routine:** Step-by-step breakdown with products (morning/night/weekly)
- **Product Carousel:** 4-8 featured products with creator commentary per item
- **Creator Quote Pulls:** Personal testimonials as visual breaks throughout
- **Before/After:** Creator's results with timeline
- **Why She Chose This:** Creator explanations for each product selection
- **Product Bundles:** "Get Sarah's Complete Routine" with bundle savings
- **Social Proof:** Creator's follower count, engagement stats, credibility markers
- **Creator's Tips Section:** Pro tips and tricks for using products
- **Community Content:** Follower results or testimonials using creator's routine
- **Limited-Time Element:** If applicable, countdown to collab end
- **CTAs:** Hero, after video, with each product, bundle section (5-7 total)
- **FAQ:** 4-5 questions (Code expiration? Products sold separately? Restock? Creator earnings disclosure?)

**Goal:** Convert influencer's engaged audience into customers by leveraging authentic creator endorsement, educational content, and exclusive discount incentives that make followers feel part of an insider community.`,
  },
  {
    icon: <Wand2 />,
    title: "Beauty Mistake Listicle Advertorial",
    description:
      "Create a listicle addressing common beauty mistakes, explaining each mistake and linking to my products that fix it with CTAs throughout.",
    prompt: `Build a beauty mistake listicle that addresses common skincare/makeup errors and positions your products as the solutions.

**TOPIC:** e.g., "7 Skincare Mistakes Making You Look Older" OR "5 Makeup Errors Ruining Your Look" OR "Why Your Routine Isn't Working"

**PRODUCTS FEATURED:** e.g., "Our serum fixes mistake #3" OR "Our primer solves issue #5"

**Reader Type:** Choose one:
- Frustrated with results (need answers)
- Beginners making common errors
- Routine optimizers (tweaking what they have)

**Tone & Style:**
- Helpful beauty advisor
- Educational not judgmental
- Empathetic to frustrations
- Solution-focused

**CTA Guidelines:** Each CTA should:
- Reference the mistake fixed ("Fix This Mistake")
- Use improvement language ("Get Better Results")
- Include product benefit ("For Lasting Hydration")
- Escalate throughout article

**Key Elements to Include:**
- **Hook:** Relatable beauty frustration
- **List Items (5-8 mistakes):** Numbered mistake → why it happens → your product as solution
- **Product Callouts:** 1-2 per mistake with "Shop Solution" CTA
- **Before/After:** Visual proof of correct vs. incorrect technique
- **Expert Tips:** How to avoid each mistake going forward
- **CTAs:** After mistake #2, after mistake #5, conclusion (5-7 total)
- **FAQ:** 3-4 questions (Skin type fit? Routine order? Results timeline?)

**Goal:** Convert frustrated beauty consumers into buyers by validating their struggles and positioning your products as the expert-recommended solutions to common mistakes.`,
  },
  {
    icon: <Droplet />,
    title: "Step-by-Step Tutorial Landing Page",
    description:
      "Build a tutorial page featuring step-by-step video guides with product callouts and 'Add Look to Cart' option with savings.",
    prompt: `Build a step-by-step tutorial landing page that educates while making it easy to purchase the complete look or routine with one click.

**TUTORIAL FOCUS:** e.g., "The 10-Minute Morning Glow Routine" OR "Smokey Eye for Beginners" OR "Korean Glass Skin Method"

**PRODUCTS FEATURED:** e.g., 4-8 products used in tutorial

**Learner Type:** Choose one:
- Beginners (learning technique)
- Routine builders (need full system)
- Product researchers (comparing options)

**Tone & Style:**
- Clear instructional voice
- Encouraging and achievable
- Expert but accessible
- "You can do this" confidence

**CTA Guidelines:** Each CTA should:
- Simplify purchase ("Get the Complete Look")
- Reference outcome ("Achieve This Glow")
- Include bundle savings ("Save $X on Full Set")
- Use step completion ("Start This Routine")

**Key Elements to Include:**
- **Hero Section:** Tutorial name, final result photo/video, difficulty level, time required, "Get This Look" CTA
- **Video Tutorial:** Main instructional video with chapters/timestamps per step
- **Step-by-Step Breakdown:** 5-10 numbered steps with text + images
- **Product Callouts Per Step:** Which product used, why it works, "Add to Cart" option
- **Complete Look Bundle:** All products in one "Add to Cart" with bundle savings
- **Before/After Comparison:** Visual transformation result
- **Alternative Options:** "Or try this if..." suggestions for variations
- **Product Details Sidebar:** All featured products with prices and ratings
- **Tips & Tricks:** Pro advice for better results
- **Common Mistakes:** What to avoid when trying this
- **Time Breakdown:** How long each step takes
- **Shopping Options:** Bundle vs. individual, kit variations
- **CTAs:** Hero, after video, with each step, bundle section, footer (6-8 total)
- **FAQ:** 4-5 questions (Beginner-friendly? Substitute products? Skin type fit? How long lasts?)

**Goal:** Convert tutorial viewers into buyers by seamlessly integrating product recommendations into educational content and offering simplified one-click purchasing of the complete routine at bundled savings.`,
  },
  {
    icon: <Smile />,
    title: "Use-Case Specific Tutorial",
    description:
      "Create a tutorial page targeting a specific use case with time estimates and product lists.",
    prompt: `Build a use-case specific tutorial page that solves a targeted beauty problem with actionable steps and required product recommendations.

**USE CASE:** e.g., "Long-Wear Makeup for 12-Hour Days" OR "Acne-Safe Routine" OR "Makeup for Video Calls" OR "Travel Beauty Essentials"

**PRODUCTS NEEDED:** e.g., 5-10 products specific to this use case

**Target User:** Choose one:
- Problem-solving (specific need to fix)
- Scenario-preparing (event or situation)
- Constraint-working (sensitive skin, time-limited, budget)

**Tone & Style:**
- Solution-focused and practical
- Empathetic to the specific challenge
- Time-conscious and efficient
- Results-oriented

**CTA Guidelines:** Each CTA should:
- Reference the use case ("Perfect for Sensitive Skin")
- Use outcome language ("Look Fresh All Day")
- Include time benefit ("Ready in 5 Minutes")
- Emphasize suitability ("Made for This")

**Key Elements to Include:**
- **Hero Section:** Use case headline, result image, total time required, "Get Started" CTA
- **The Problem:** Brief description of why this use case needs special approach
- **What You'll Need:** Complete product list with images and prices
- **Step-by-Step Tutorial:** 4-8 numbered steps with time per step
- **Product Integration:** Which products used at each step with why they work for this case
- **Time Breakdown:** Visual timeline showing total duration
- **Key Features for Use Case:** Why these specific products suit this scenario
- **Before/After:** Results specific to this use case
- **Pro Tips:** Use-case specific advice
- **Common Issues:** Troubleshooting for this scenario
- **Complete Bundle:** All products packaged for this use case with savings
- **Alternative Products:** "If you have [constraint], try this instead"
- **CTAs:** Hero, after product list, with bundle, footer (5-6 total)
- **FAQ:** 4-5 questions (Works for my [constraint]? How long lasts? Substitutions? Price?)

**Goal:** Convert users with specific beauty challenges into buyers by providing targeted solutions that directly address their unique use case with appropriate products, realistic time expectations, and proven results.`,
  },
  {
    icon: <Palette />,
    title: "Ingredient Transparency Page",
    description:
      "Build an educational page explaining harmful ingredients with links to research, showcasing my clean formulations with transparent ingredient lists and CTAs.",
    prompt: `Build an ingredient transparency page that educates on harmful chemicals while positioning your clean formulations as the safe, science-backed alternative.

**TRANSPARENCY FOCUS:** e.g., "What's Really in Your Skincare" OR "The Ingredients We'll Never Use" OR "Clean Beauty, Explained"

**FORMULATION ANGLE:** e.g., "Our toxin-free promise" OR "Safe ingredients only"

**Reader Type:** Choose one:
- Health-conscious shoppers (researching ingredients)
- Sensitive skin sufferers (avoiding irritants)
- Clean beauty converts (seeking trustworthy brands)

**Tone & Style:**
- Educational without fear-mongering
- Scientific but accessible
- Transparent and honest
- Empowering through knowledge

**CTA Guidelines:** Each CTA should:
- Emphasize clean formulation ("Shop Toxin-Free")
- Reference transparency ("See Full Ingredients")
- Use safety language ("Safe for Sensitive Skin")
- Include certification badges

**Key Elements to Include:**
- **Hero Section:** Bold transparency statement, "Never" list preview, "Shop Clean" CTA
- **Our Clean Commitment:** What clean beauty means to you (2-3 paragraphs)
- **Never List:** 10-15 harmful ingredients you exclude with brief why-harmful for each
- **Ingredient Deep Dives (5-8):** Detailed explanations of worst offenders with research links
- **What We Use Instead:** Safe alternatives for each harmful ingredient
- **Full Ingredient Philosophy:** Sourcing standards, testing, formulation principles
- **Product Ingredient Lists:** Transparent disclosure for each product with explanations
- **Comparison Chart:** Toxic ingredients in typical products vs. your clean formulas
- **Certifications Displayed:** Clean beauty seals, dermatologist-tested, cruelty-free badges
- **Research References:** Links to studies and scientific sources (5-10 citations)
- **Ingredient Glossary:** Educational explainers for key ingredients you do use
- **Dermatologist Quotes:** Expert validation of your formulation approach
- **Safe for Section:** Sensitive skin, pregnancy-safe, eczema-friendly callouts
- **CTAs:** Hero, after never list, with products, after certifications (5-6 total)
- **FAQ:** 5-7 questions (How verified? All products clean? Pregnancy-safe? Testing methods?)

**Goal:** Convert ingredient-conscious shoppers into loyal customers by providing radical transparency, scientific education, and proof that your clean formulations are both safe and effective - building unshakeable trust through honesty.`,
  },
  {
    icon: <Clock />,
    title: "Certification Education Advertorial",
    description:
      "Create an advertorial clarifying confusing beauty certifications and positioning my brand as genuinely ethical with certification badges, audit proof, and verifications.",
    prompt: `Build a certification education advertorial that cuts through greenwashing confusion and positions your brand as genuinely certified and ethical.

**TOPIC:** e.g., "Not All 'Clean Beauty' Labels Are Equal: What Actually Matters" OR "The Certifications That Actually Mean Something" OR "How to Spot Fake Clean Beauty Claims"

**YOUR CERTIFICATIONS:** e.g., "We're certified Leaping Bunny, EWG Verified, B-Corp"

**Reader Type:** Choose one:
- Confused shoppers (overwhelmed by claims)
- Skeptical of greenwashing (burnt by fake claims)
- Values-driven buyers (want verified ethics)

**Tone & Style:**
- Honest educator exposing industry tricks
- Critical of false claims but not preachy
- Empowering through knowledge
- Confident in your genuine certifications

**CTA Guidelines:** Each CTA should:
- Reference real certification ("Shop Verified Clean")
- Use trust language ("Third-Party Certified")
- Include badge visuals nearby
- Emphasize verification ("Audit Proven")

**Key Elements to Include:**
- **Hook:** Expose greenwashing problem (2-3 paragraphs on fake claims)
- **The Certification Problem:** Why most "clean" claims are meaningless
- **What Actually Matters:** 5-7 legitimate certifications explained
- **Certification Deep Dives:** For each real cert: what it requires, who verifies, why it's legit
- **Red Flags to Spot:** How to identify fake or self-made certifications
- **Our Certifications:** Which legitimate seals you hold with proof
- **Audit Documentation:** Links to verification, certificate numbers, audit dates
- **Certification Comparison Chart:** Real certs vs. marketing terms
- **The Certification Process:** What you went through to earn each (builds credibility)
- **Verifier Information:** Links to third-party organizations who certified you
- **Product-Level Proof:** Which products carry which certifications
- **Competitor Analysis:** "Only X% of brands are actually certified"
- **Your Commitment Statement:** Why you invested in real verification
- **CTAs:** After greenwashing expose, with certification explanations, with products (5-6 total)
- **FAQ:** 4-5 questions (How to verify? Cost to certify? Re-certification? All products certified?)

**Goal:** Convert skeptical, educated shoppers into loyal customers by exposing industry greenwashing, teaching them to identify real certifications, and proving your brand's genuine third-party verified ethics - positioning you as the trustworthy alternative.`,
  },
  {
    icon: <Leaf />,
    title: "Before/After Transformation Gallery",
    description:
      "Create a UGC gallery featuring customer-submitted before/after photos organized by concern, with transformation timelines and filtering by skin type, age, or concern.",
    prompt: `Build a before/after transformation gallery showcasing real customer results organized by skin concerns to provide authentic proof of product efficacy.

**GALLERY FOCUS:** e.g., "Real Results from Real People" OR "Customer Transformations" OR "See the Difference"

**TRANSFORMATION CATEGORIES:** e.g., Acne, wrinkles, dark spots, texture, redness, dryness

**Gallery Visitor Type:** Choose one:
- Skeptical shoppers (need proof it works)
- Concern-specific (solving particular issue)
- Researching effectiveness (comparing options)

**Tone & Style:**
- Authentic and unfiltered
- Celebratory of real progress
- Empathetic to skin struggles
- Results-focused not perfection-focused

**CTA Guidelines:** Each CTA should:
- Reference specific results ("Get Clearer Skin")
- Use transformation language ("Start Your Journey")
- Include timeline context ("See Results in 4 Weeks")
- Link to concern-specific products

**Key Elements to Include:**
- **Hero Section:** Headline, before/after preview grid, filter options, "Submit Your Story" CTA
- **Filter Options:** By concern (acne, aging, texture, etc.), by skin type, by age range, by timeline, verified only
- **Before/After Grid:** Side-by-side images with hover details (name, concern, timeline, products)
- **Transformation Stories (4-6 featured):** Large B/A images, customer name, full journey (2-3 paragraphs), timeline, routine used, "Shop Her Routine" CTA
- **Timeline Breakdown:** Visual showing results at weeks 2, 4, 8, 12
- **Concern-Specific Galleries:** Dedicated sections per skin issue with filtered results
- **Product Routines Displayed:** Which products each person used for their results
- **Transformation Metrics:** "3,200+ real transformations" or "Average improvement in 6 weeks"
- **Skin Type Labels:** Oily, dry, combination, sensitive tags per photo
- **Age Range Tags:** 20s, 30s, 40s, 50+ for relatable results
- **Photo Submission Section:** "Share your B/A & get 25% off" with upload
- **Authenticity Badges:** Verified purchase, unedited photo indicators
- **CTAs:** Top of page, with featured stories, concern galleries (5-6 total)
- **FAQ:** 3-4 questions (How to submit? Privacy? Photo requirements? Incentive?)

**Goal:** Convert skeptical shoppers into confident buyers by showcasing authentic, concern-specific customer transformations with realistic timelines that prove your products deliver real results for people with similar skin issues.`,
  },
  {
    icon: <Heart />,
    title: "Flash Sale Landing Page",
    description:
      "Build a sale page with countdown timers, bestseller grids showing discounted prices, and urgency messaging, with cross-sell recommendations to maximize AOV.",
    prompt: `Build a flash sale landing page that drives urgency and maximizes order value through time-limited offers, strategic bundling, and smart cross-sells.

**SALE FOCUS:** e.g., "24-Hour Beauty Sale" OR "Weekend Flash: Up to 50% Off" OR "Lightning Deal Event"

**DISCOUNT STRUCTURE:** e.g., "30-50% off bestsellers" OR "Buy 2 Get 40%, Buy 3 Get 50%"

**Shopper Type:** Choose one:
- Deal hunters (motivated by discounts)
- Stock-up shoppers (buying multiples)
- Impulse buyers (time pressure converts)

**Tone & Style:**
- Urgent and exciting
- High-energy without desperation
- Clear about limited time
- Value-focused celebration

**CTA Guidelines:** Each CTA should:
- Emphasize scarcity ("Ends in X Hours")
- Reference savings ("Save 50% Now")
- Use urgency language ("Last Chance")
- Update with time remaining

**Conversion Tactics Use:**
- Prominent countdown timer (multiple placements)
- Tiered discounts for bulk buying
- Bestseller social proof
- Limited stock indicators
- "Selling fast" live notifications
- Bundle recommendations for higher AOV

**Key Elements to Include:**
- **Hero Section:** Sale headline with max discount, countdown timer, "Shop Sale" CTA
- **Countdown Timer:** Large hours:minutes:seconds (sticky top bar)
- **Tiered Discount Banner:** "Buy 2: 40% | Buy 3: 50% | Buy 4+: 60%" with progress bar
- **Bestseller Grid:** 12-20 products with discount badges, strikethrough prices, sale prices
- **Filter Options:** By discount %, by category, by bestseller rank, in-stock only
- **Product Cards:** Discount badge, before/after price, star rating, "Add to Cart" quick-add
- **Urgency Indicators:** "Selling fast" or "Low stock" tags on popular items
- **Social Proof Ticker:** Live purchases ("Sarah just bought this 2 min ago")
- **Bundle Suggestions:** "Complete your routine" cross-sells with bundle savings
- **Discount Progress (Sidebar):** Current tier + items needed for next level
- **Free Shipping Bar:** Progress to threshold with suggested add-ons
- **Flash Deal Spotlights:** 3-5 hero products with deepest discounts
- **Sale Terms:** Clear "Limited time only" and any exclusions
- **CTAs:** Hero, on each product, bundle sections, sticky checkout (always visible)
- **FAQ:** 3-4 questions (How long? Returns? Stackable codes? Restock after?)

**Goal:** Maximize revenue during limited window by creating urgency through countdown timers and scarcity messaging while driving higher AOV through tiered bulk discounts and strategic cross-selling.`,
  },
];
